import {
  computeOwnerDebtHistory,
  localTodayIso,
  ownersWithDebtHistory,
  type CashbookDebtEntryRecord,
  type CashbookSettings,
  UNASSIGNED_DEBT_OWNER_KEY
} from '@finvox/shared/cashbook';
import { toMoney } from '@finvox/shared/money';
import { format, parseISO } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DebtOwnerSelect } from '@/components/cashbook/DebtOwnerSelect';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import { displayCashbookEntryLabel } from '@/lib/cashbookEntryLabel';

type Props = {
  open: boolean;
  onClose: () => void;
  settings: CashbookSettings;
  debtEntries: CashbookDebtEntryRecord[];
  asOfDate?: string;
};

export function CashbookDebtHistoryDialog({
  open,
  onClose,
  settings,
  debtEntries,
  asOfDate = localTodayIso()
}: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('ar') ? ar : enUS;
  const isArabic = i18n.language.startsWith('ar');
  const [ownerKey, setOwnerKey] = useState('');

  const historyOwners = useMemo(
    () => ownersWithDebtHistory(settings, debtEntries, asOfDate),
    [settings, debtEntries, asOfDate]
  );

  const ownerLabels = useMemo(
    () => ({
      [UNASSIGNED_DEBT_OWNER_KEY]: t('cashbook.debts.unassignedOpeningDebt')
    }),
    [t]
  );

  const rows = useMemo(() => {
    if (!ownerKey.trim()) return [];
    return computeOwnerDebtHistory(settings, debtEntries, asOfDate, ownerKey.trim());
  }, [settings, debtEntries, asOfDate, ownerKey]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cashbook-debt-history-title"
    >
      <Card className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden p-6 shadow-lg">
        <div className="shrink-0">
          <h2 id="cashbook-debt-history-title" className="text-lg font-semibold">
            {t('cashbook.debts.historyTitle')}
          </h2>
          <p className="mt-1 text-sm text-[var(--fv-text-muted)]">{t('cashbook.debts.historyHint')}</p>

          <div className="mt-4 max-w-md">
            <label className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.debts.creditor')}</label>
            <DebtOwnerSelect
              className="mt-1 w-full"
              value={ownerKey}
              onValueChange={setOwnerKey}
              owners={historyOwners}
              ownerLabels={ownerLabels}
              allowCreate={false}
              placeholder={t('cashbook.debts.selectOwner')}
            />
          </div>
        </div>

        {!ownerKey.trim() ? (
          <p className="mt-4 text-sm text-[var(--fv-text-muted)]">{t('cashbook.debts.historySelectOwner')}</p>
        ) : rows.length === 0 ? (
          <p className="mt-4 text-sm text-[var(--fv-text-muted)]">{t('cashbook.debts.historyEmpty')}</p>
        ) : (
          <div className="mt-4 min-h-0 flex-1 overflow-auto rounded-lg border border-[var(--fv-border)]">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-[1] text-[var(--fv-text-muted)]">
                <tr>
                  <th className="bg-[var(--fv-bg)] px-3 py-2 text-start font-medium">{t('cashbook.colDate')}</th>
                  <th className="bg-[var(--fv-bg)] px-3 py-2 text-start font-medium">{t('cashbook.debts.historyType')}</th>
                  <th className="bg-[var(--fv-bg)] px-3 py-2 text-start font-medium">{t('cashbook.category')}</th>
                  <th className="bg-[var(--fv-bg)] px-3 py-2 text-start font-medium">{t('cashbook.memo')}</th>
                  <th className="bg-[var(--fv-bg)] px-3 py-2 text-end font-medium">{t('cashbook.amount')}</th>
                  <th className="bg-[var(--fv-bg)] px-3 py-2 text-end font-medium">
                    {t('cashbook.debts.colRunningRemaining')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--fv-border)]">
                {rows.map((row) => {
                  const negative = toMoney(row.signedAmount).lt(0);
                  const categoryLabel =
                    row.kind === 'opening'
                      ? t('cashbook.debts.openingRow')
                      : row.kind === 'payment'
                        ? t('cashbook.categories.debt_payment')
                        : t(`cashbook.debts.categories.${row.categoryKey}`, {
                            defaultValue: row.categoryKey
                          });
                  const memoRaw = row.memo?.trim();
                  const memoLabel = memoRaw
                    ? displayCashbookEntryLabel(memoRaw, t, isArabic)
                    : '—';
                  return (
                    <tr key={`${row.kind}-${row.id}`}>
                      <td className="px-3 py-2 whitespace-nowrap align-top">
                        {format(parseISO(row.date), 'PP', { locale })}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {t(`cashbook.debts.historyKinds.${row.kind}`)}
                      </td>
                      <td className="px-3 py-2 align-top">{categoryLabel}</td>
                      <td className="px-3 py-2 text-[var(--fv-text-muted)] align-top">
                        <div>{memoLabel}</div>
                        {row.kind === 'payment' && row.allocations && row.allocations.length > 0 ? (
                          <ul className="mt-1 space-y-0.5 text-xs">
                            {row.allocations.map((a) => {
                              const lineLabel = a.missing
                                ? t('cashbook.debts.historyAllocMissing')
                                : a.source === 'opening_debt'
                                  ? t('cashbook.debts.openingRow')
                                  : t(`cashbook.debts.categories.${a.categoryKey ?? 'other'}`, {
                                      defaultValue: a.categoryKey ?? '—'
                                    });
                              return (
                                <li key={`${row.id}-${a.debtEntryId}`} className="tabular-nums">
                                  {lineLabel}
                                  {a.date ? ` (${format(parseISO(a.date), 'PP', { locale })})` : ''}
                                  {': '}
                                  <MoneyDisplay value={a.amount} />
                                </li>
                              );
                            })}
                          </ul>
                        ) : null}
                      </td>
                      <td
                        className={`px-3 py-2 text-end tabular-nums align-top ${
                          negative ? 'text-[var(--fv-danger)]' : ''
                        }`}
                      >
                        <MoneyDisplay value={row.signedAmount} />
                      </td>
                      <td className="px-3 py-2 text-end font-medium tabular-nums align-top">
                        <MoneyDisplay value={row.runningRemaining} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 flex shrink-0 justify-end">
          <Button
            variant="secondary"
            onClick={() => {
              setOwnerKey('');
              onClose();
            }}
          >
            {t('common.cancel')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
